import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Circle, PlayCircle, CheckCircle } from 'lucide-react';
import { Task, Project } from '@/types/product-manager';
import TaskCard from './TaskCard';

interface TaskBoardProps {
  tasks: Task[];
  projects: Project[];
  onTaskUpdate: () => void;
  activeTab: 'todo' | 'in-progress' | 'completed';
}

const TaskBoard: React.FC<TaskBoardProps> = ({
  tasks,
  projects,
  onTaskUpdate,
  activeTab
}) => {
  const getProjectById = (projectId: string) => projects.find(p => p.id === projectId);

  const getTabInfo = (tab: string) => {
    switch (tab) {
      case 'todo':
        return {
          title: 'To Do',
          icon: Circle,
          iconColor: 'text-gray-500',
          count: tasks.filter(t => t.status === 'todo').length
        };
      case 'in-progress':
        return {
          title: 'In Progress',
          icon: PlayCircle,
          iconColor: 'text-blue-500',
          count: tasks.filter(t => t.status === 'in-progress').length
        };
      case 'completed':
        return {
          title: 'Completed',
          icon: CheckCircle,
          iconColor: 'text-green-500',
          count: tasks.filter(t => t.status === 'completed').length
        };
      default:
        return {
          title: 'To Do',
          icon: Circle,
          iconColor: 'text-gray-500',
          count: 0
        };
    }
  };

  // Filter tasks based on active tab
  const filteredTasks = tasks.filter(task => task.status === activeTab);
  const tabInfo = getTabInfo(activeTab);
  const Icon = tabInfo.icon;

  return (
    <div className="space-y-4">
      {/* Tab Header */}
      <div className="flex items-center space-x-2 mb-6">
        <Icon className={`h-6 w-6 ${tabInfo.iconColor}`} />
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {tabInfo.title}
        </h2>
        <Badge variant="secondary" className="text-sm">
          {filteredTasks.length}
        </Badge>
      </div>

      {/* Task Grid */}
      {filteredTasks.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Icon className={`h-16 w-16 mx-auto mb-4 ${tabInfo.iconColor} opacity-50`} />
          <h3 className="text-lg font-medium mb-2">No {tabInfo.title.toLowerCase()} tasks</h3>
          <p className="text-sm">
            {activeTab === 'todo' 
              ? 'Create a new task to get started.' 
              : `No tasks are currently ${tabInfo.title.toLowerCase()}.`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {filteredTasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              project={getProjectById(task.projectId)}
              projects={projects}
              onUpdate={onTaskUpdate}
              viewMode="card"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TaskBoard;